<template>
  <div class="p-6 mx-auto">
    <PageHeading
      class="mb-6"
      title="Dashboard Documentation"
      description="Learn how to use the Dashboard to monitor your events and system statistics."
      :crumbs="[{ label: 'Documentation' }, { label: 'Dashboard' }]"
    />

    <div class="space-y-6">
      <!-- Overview Section -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Overview</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The Dashboard provides a comprehensive overview of your jukebox system, including active
          events, upcoming events, song statistics, and system-wide metrics.
        </p>
      </section>

      <!-- Active Event Banner -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Active Event Banner
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          When an event is currently active (voting is open), a prominent banner appears at the top
          of the dashboard showing:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Event name and location</li>
          <li>Event description</li>
          <li>A "Go to the event" button that takes you to the Live Event control panel</li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400">
          Click the button to manage the live event, control song playback, and monitor votes in
          real-time.
        </p>
      </section>

      <!-- Next Upcoming Event Card -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Next Upcoming Event
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          This card displays information about the next scheduled event, including:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li><strong>Event Date:</strong> When the event will take place</li>
          <li><strong>Voting Starts:</strong> When voting opens for the event</li>
          <li><strong>Voting Ends:</strong> When voting closes (if set)</li>
          <li><strong>Organizer:</strong> The person organizing the event</li>
          <li><strong>Description:</strong> Additional details about the event</li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400">
          Click on the card to navigate to the Events page where you can manage all events.
        </p>
      </section>

      <!-- Song Status Card -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Song Status</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          This card provides statistics about your song library:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li><strong>Active Songs:</strong> Number of songs available for voting</li>
          <li><strong>Disabled Songs:</strong> Number of songs that are currently disabled</li>
          <li><strong>Total Songs:</strong> Total number of songs in your library</li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          Each category shows both the count and the percentage of the total. A warning appears if
          more than 10% of songs are disabled.
        </p>
        <p class="text-gray-600 dark:text-gray-400">
          Click on the card to navigate to the Songs page where you can manage your song library.
        </p>
      </section>

      <!-- Stats Cards -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Statistics Cards</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The dashboard displays four key metrics at a glance:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li><strong>Events:</strong> Total number of events created (all time)</li>
          <li><strong>Songs:</strong> Total number of songs in the library, with active count</li>
          <li><strong>Users:</strong> Total number of registered users</li>
          <li><strong>Votes:</strong> Total number of votes cast (all time)</li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400">
          These metrics provide a quick overview of your system's usage and growth.
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import PageHeading from "../../components/common/PageHeading.vue";

export default {
  name: "DashboardDocumentation",
  components: { PageHeading }
};
</script>
